import api from './axiosInstance';
import { Asset } from '../types/asset';

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  meta: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Asset Request/Response Types
interface CreateAssetRequest {
  name: string;
  location?: string;
  projectTypeId: string; // ObjectId as string
  description?: string;
  specification?: {
    model?: string;
    serialNumber?: string;
    manufacturer?: string;
    capacity?: string;
    dimensions?: string;
    weight?: string;
    materials?: string[];
    operatingConditions?: string;
    performanceSpecs?: string;
    certifications?: string[];
  };
  maintenance?: {
    schedule?: string;
    lastMaintenanceDate?: Date;
    nextMaintenanceDate?: Date;
    instructions?: string;
    requiredTools?: string[];
    safetyPrecautions?: string[];
  };
}

interface UpdateAssetRequest {
  name?: string;
  location?: string;
  projectTypeId?: string;
  status?: 'active' | 'inactive';
  description?: string;
  specification?: {
    model?: string;
    serialNumber?: string;
    manufacturer?: string;
    capacity?: string;
    dimensions?: string;
    weight?: string;
    materials?: string[];
    operatingConditions?: string;
    performanceSpecs?: string;
    certifications?: string[];
  };
  maintenance?: {
    schedule?: string;
    lastMaintenanceDate?: Date;
    nextMaintenanceDate?: Date;
    instructions?: string;
    requiredTools?: string[];
    safetyPrecautions?: string[];
  };
}

interface AssetFilterParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  search?: string;
  name?: string;
  projectTypeId?: string;
  status?: 'active' | 'inactive';
  assetStatus?: 'active' | 'inactive';
  location?: string;
  populate?: string;
}

export const assetService = {
  // Get all assets with filtering and pagination
  getAssets: async (params?: AssetFilterParams): Promise<{ assets: Asset[]; totalDocs: number; pagination?: any }> => {
    try {
      const response = await api.get<PaginatedResponse<Asset>>('/assets', { params });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch assets');
      }
      
      return {
        assets: response.data.data,
        totalDocs: response.data.meta.totalDocs,
        pagination: {
          page: response.data.meta.page,
          limit: response.data.meta.limit,
          totalPages: response.data.meta.totalPages,
          hasNextPage: response.data.meta.hasNextPage,
          hasPrevPage: response.data.meta.hasPrevPage,
          nextPage: response.data.meta.nextPage,
          prevPage: response.data.meta.prevPage,
        }
      };
    } catch (error: any) {
      console.error('Error fetching assets:', error);
      throw new Error(error.message || 'Failed to fetch assets');
    }
  },

  // Get asset by ID
  getAssetById: async (id: string): Promise<Asset> => {
    try {
      const response = await api.get<ApiResponse<Asset>>(`/assets/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch asset');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching asset:', error);
      throw new Error(error.message || 'Failed to fetch asset');
    }
  },

  // Create new asset
  createAsset: async (assetData: CreateAssetRequest): Promise<Asset> => {
    try {
      // Validate required fields
      if (!assetData.name || !assetData.projectTypeId) {
        throw new Error('Name and project type are required');
      }

      const response = await api.post<ApiResponse<Asset>>('/assets', assetData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create asset');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating asset:', error);
      throw new Error(error.message || 'Failed to create asset');
    }
  },

  // Update asset
  updateAsset: async (id: string, assetData: UpdateAssetRequest): Promise<Asset> => {
    try {
      const response = await api.patch<ApiResponse<Asset>>(`/assets/${id}`, assetData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update asset');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error updating asset:', error);
      throw new Error(error.message || 'Failed to update asset');
    }
  },

  // Delete asset
  deleteAsset: async (id: string): Promise<void> => {
    try {
      const response = await api.delete<ApiResponse<null>>(`/assets/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete asset');
      }
    } catch (error: any) {
      console.error('Error deleting asset:', error);
      throw new Error(error.message || 'Failed to delete asset');
    }
  },

  // Get assets by project type
  getAssetsByProjectType: async (projectTypeId: string, params?: AssetFilterParams): Promise<{ assets: Asset[]; totalDocs: number; pagination?: any }> => {
    try {
      const response = await api.get(`/assets/project/${projectTypeId}`, { params });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch assets by project type');
      }
      
      // Handle the new response structure
      let assetsData: Asset[] = [];
      let totalDocs = 0;
      let pagination = null;
      
      if (response.data.data && Array.isArray(response.data.data)) {
        // Direct array response
        assetsData = response.data.data;
        totalDocs = response.data.data.length;
      } else if (response.data.data && response.data.meta) {
        // New structure with meta
        assetsData = response.data.data;
        totalDocs = response.data.meta.totalDocs || response.data.data.length;
        pagination = response.data.meta;
      } else if (response.data.data && response.data.data.results) {
        // Old paginated structure
        assetsData = response.data.data.results;
        totalDocs = response.data.data.totalResults;
        pagination = {
          page: response.data.data.page,
          limit: response.data.data.limit,
          totalPages: response.data.data.totalPages,
          hasNextPage: response.data.data.hasNextPage,
          hasPrevPage: response.data.data.hasPrevPage,
          nextPage: response.data.data.nextPage,
          prevPage: response.data.data.prevPage,
        };
      }
      
      return {
        assets: assetsData,
        totalDocs,
        pagination
      };
    } catch (error: any) {
      console.error('Error fetching assets by project type:', error);
      throw new Error(error.message || 'Failed to fetch assets by project type');
    }
  },

  // Toggle asset status (if backend supports this)
  toggleAssetStatus: async (id: string): Promise<Asset> => {
    try {
      const response = await api.patch<ApiResponse<Asset>>(`/assets/${id}/toggle`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to toggle asset status');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error toggling asset status:', error);
      // If the endpoint doesn't exist, fall back to regular update
      if (error.code === 404) {
        const asset = await assetService.getAssetById(id);
        const newStatus = asset.status === 'active' ? 'inactive' : 'active';
        return await assetService.updateAsset(id, { status: newStatus });
      }
      throw new Error(error.message || 'Failed to toggle asset status');
    }
  },

  // Get assets for dropdown (with basic info)
  getAssetsForDropdown: async (): Promise<Asset[]> => {
    try {
      console.log('Fetching assets from /assets/all endpoint'); // Debug: Log endpoint
      const response = await api.get<{ data: Asset[] }>('/assets/all');
      console.log('Assets API response:', response.data); // Debug: Log full response
      
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching assets for dropdown:', error);
      // Fallback to paginated method
      try {
        console.log('Trying fallback to /assets endpoint'); // Debug: Log fallback
        const fallbackResponse = await api.get<PaginatedResponse<Asset>>('/assets', { 
          params: { 
            limit: 100, 
            sortBy: 'name',
            status: 'active' 
          } 
        });
        
        console.log('Fallback response:', fallbackResponse.data); // Debug: Log fallback response
        
        if (!fallbackResponse.data.success) {
          throw new Error(fallbackResponse.data.message || 'Failed to fetch assets for dropdown');
        }
        
        return fallbackResponse.data.data;
      } catch (fallbackError: any) {
        console.error('Fallback assets fetch also failed:', fallbackError);
        throw new Error(fallbackError.message || 'Failed to fetch assets for dropdown');
      }
    }
  },

  // Get assets for dropdown filtered by project type
  getAssetsForDropdownByProjectType: async (projectTypeId: string): Promise<Asset[]> => {
    try {
      const response = await api.get<PaginatedResponse<Asset>>('/assets', { 
        params: { 
          projectTypeId,
          limit: 100, 
          sortBy: 'name',
          status: 'active',
          assetStatus: 'active'
        } 
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch assets for dropdown');
      }
      
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching assets for dropdown by project type:', error);
      // Fallback to get all assets and filter client-side
      try {
        const allAssets = await assetService.getAssetsForDropdown();
        return allAssets.filter(asset => asset.projectTypeId === projectTypeId);
      } catch (fallbackError: any) {
        console.error('Fallback assets fetch also failed:', fallbackError);
        throw new Error(fallbackError.message || 'Failed to fetch assets for dropdown by project type');
      }
    }
  },

  // Get all assets without pagination (for dropdowns)
  getAllAssets: async (): Promise<Asset[]> => {
    try {
      const response = await api.get<{ data: Asset[] }>('/assets/all');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching all assets:', error);
      // Fallback to paginated method
      try {
        const fallbackResponse = await api.get<PaginatedResponse<Asset>>('/assets', { 
          params: { 
            limit: 1000, 
            sortBy: 'name',
            status: 'active' 
          } 
        });
        
        if (!fallbackResponse.data.success) {
          throw new Error(fallbackResponse.data.message || 'Failed to fetch assets');
        }
        
        return fallbackResponse.data.data;
      } catch (fallbackError: any) {
        console.error('Fallback assets fetch also failed:', fallbackError);
        throw new Error(fallbackError.message || 'Failed to fetch assets');
      }
    }
  },

  // Get assets by project ID (fallback method)
  getAssetsByProject: async (projectId: string): Promise<Asset[]> => {
    try {
      // First try to get all assets and filter by project
      const response = await api.get<PaginatedResponse<Asset>>('/assets', { 
        params: { 
          limit: 1000, 
          sortBy: 'name',
          status: 'active',
          assetStatus: 'active'
        } 
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch assets');
      }
      
      // Filter assets that belong to the project (if project field exists)
      const allAssets = response.data.data || [];
      const projectAssets = allAssets.filter(asset => 
        asset.project === projectId || 
        asset.projectId === projectId ||
        (asset.project && asset.project._id === projectId) ||
        (asset.project && asset.project.id === projectId)
      );
      
      return projectAssets;
    } catch (error: any) {
      console.error('Error fetching assets by project:', error);
      throw new Error(error.message || 'Failed to fetch assets by project');
    }
  },

  // Get assets with project type details (using populate)
  getAssetsWithProjectTypes: async (params?: AssetFilterParams): Promise<{ assets: Asset[]; totalDocs: number; pagination?: any }> => {
    try {
      const queryParams = { ...params, populate: 'projectTypeId' };
      const response = await api.get<PaginatedResponse<Asset>>('/assets', { 
        params: queryParams
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch assets with project types');
      }
      
      return {
        assets: response.data.data,
        totalDocs: response.data.meta.totalDocs,
        pagination: {
          page: response.data.meta.page,
          limit: response.data.meta.limit,
          totalPages: response.data.meta.totalPages,
          hasNextPage: response.data.meta.hasNextPage,
          hasPrevPage: response.data.meta.hasPrevPage,
          nextPage: response.data.meta.nextPage,
          prevPage: response.data.meta.prevPage,
        }
      };
    } catch (error: any) {
      console.error('Error fetching assets with project types:', error);
      throw new Error(error.message || 'Failed to fetch assets with project types');
    }
  },

  // Utility function to validate asset data
  validateAssetData: (assetData: CreateAssetRequest | UpdateAssetRequest): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if ('name' in assetData && assetData.name && assetData.name.trim().length < 2) {
      errors.push('Asset name must be at least 2 characters long');
    }

    if ('name' in assetData && assetData.name && assetData.name.trim().length > 200) {
      errors.push('Asset name must not exceed 200 characters');
    }

    if ('projectTypeId' in assetData && assetData.projectTypeId && !/^[0-9a-fA-F]{24}$/.test(assetData.projectTypeId)) {
      errors.push('Invalid project type ID format');
    }

    if ('description' in assetData && assetData.description && assetData.description.length > 1000) {
      errors.push('Description must not exceed 1000 characters');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}; 